<template>
<div class="main-container">
    <div class="inner-container">
        <signed-in-user-info :user="user" :userData="userData" :loadingUser="loadingUser"/>
        <div class="track-shipment update-user cp-wrapper">
            <div class="cp-card" ref="createCard">
                <div class="cp-header">
                    <div class="cp-header-icon">
                        <svg id="Layer_2" data-name="Layer 2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 159.75 148.87">
                            <g id="Layer_1-2" data-name="Layer 1">
                                <path d="M154.27,12.53c.87-.17,1.78,0,2.54,.46,1.29,.53,2.2,1.79,2.2,3.28l.74,103.46c.07,1.27-.56,2.54-1.73,3.22l-42.25,25.21c-.6,.46-1.34,.72-2.15,.72-.2,0-.39-.01-.57-.05L3.39,136.62c-1.89-.08-3.39-1.64-3.39-3.55V27.85H0c-.04-1.4,.75-2.77,2.13-3.37L57.14,.31h0c.57-.26,1.22-.36,1.9-.27l95.23,12.49h0Zm-37.27,30.93v96.48l36-21.47-.7-97.26-35.3,22.25h0Zm-7,97.91V45l-46.8-5.97-1.54,44.72-16.43-11.19-16.43,9.29,3.4-46.78-25.19-3.21v98.07l103,11.44h0ZM102.56,12.88l-34.52,19.59,44.9,5.72,31.47-19.83-41.86-5.49h0ZM36.56,28.46L70.02,8.61l-11.03-1.44L16.4,25.89l20.15,2.56h0Z"/>
                            </g>
                        </svg>
                    </div>
                    <div>
                        <h2 class="cp-title">Create Product</h2>
                        <p class="cp-subtitle">Pick an existing value from your products or type a new one.</p>
                    </div>
                </div>

                <div class="cp-section">
                    <h3 class="cp-section-title">Identity</h3>
                    <div class="cp-grid">
                        <div class="cp-field">
                            <label>Product Code</label>
                            <input type="text" list="createProductCodeList" v-model="productInput.productCode" @input="sanitizeLettersUpper('productCode')" placeholder="e.g. STD">
                            <datalist id="createProductCodeList">
                                <option v-for="(code, index) in distinctProductCodes" :key="index" :value="code" />
                            </datalist>
                        </div>
                        <div class="cp-field">
                            <label>Service Code</label>
                            <input type="text" list="createServiceCodeList" v-model="productInput.serviceCode" @input="sanitizeLettersUpper('serviceCode')" placeholder="e.g. DEL">
                            <datalist id="createServiceCodeList">
                                <option v-for="(code, index) in distinctServiceCodes" :key="index" :value="code" />
                            </datalist>
                        </div>
                        <div class="cp-field">
                            <label>Product Name</label>
                            <input type="text" list="createProductNameList" v-model="productInput.productName" placeholder="Product name">
                            <datalist id="createProductNameList">
                                <option v-for="(name, index) in distinctProductNames" :key="index" :value="name" />
                            </datalist>
                        </div>
                    </div>
                </div>

                <div class="cp-section">
                    <h3 class="cp-section-title">Classification</h3>
                    <div class="cp-grid">
                        <div class="cp-field">
                            <label>Service Network</label>
                            <input type="text" list="createServiceNetworkList" v-model="productInput.serviceNetwork" @input="sanitizeLettersUpper('serviceNetwork')" placeholder="Service network">
                            <datalist id="createServiceNetworkList">
                                <option v-for="(network, index) in distinctServiceNetworks" :key="index" :value="network" />
                            </datalist>
                        </div>
                        <div class="cp-field">
                            <label>Vendor</label>
                            <input type="text" list="createVendorList" v-model="productInput.vendor" @input="sanitizeLettersUpper('vendor')" placeholder="Vendor">
                            <datalist id="createVendorList">
                                <option v-for="(vendor, index) in distinctVendors" :key="index" :value="vendor" />
                            </datalist>
                        </div>

                        <div v-if="productInput.additionalService == false" class="cp-field">
                            <label>Product Family</label>
                            <input type="text" list="createProductFamilyList" v-model="productInput.productFamily" placeholder="Product family">
                            <datalist id="createProductFamilyList">
                                <option v-for="(family, index) in distinctProductFamilies" :key="index" :value="family" />
                            </datalist>
                        </div>
                        <div v-if="productInput.additionalService" class="cp-field">
                            <label>Service Group</label>
                            <input type="text" list="createServiceGroupList" v-model="productInput.serviceGroup" placeholder="Service group">
                            <datalist id="createServiceGroupList">
                                <option v-for="(group, index) in distinctServiceGroups" :key="index" :value="group" />
                            </datalist>
                        </div>
                        <div v-if="productInput.additionalService == false" class="cp-field">
                            <label>Product Sequence (auto)</label>
                            <input disabled type="text" v-model="productSequence" placeholder="Auto">
                        </div>
                        <div v-if="productInput.serviceGroup" class="cp-field">
                            <label>Service Group Sequence (auto)</label>
                            <input type="text" disabled v-model="serviceGroupSequence" placeholder="Auto">
                        </div>
                    </div>
                </div>

                <div v-if="productInput.additionalService" class="cp-section">
                    <h3 class="cp-section-title">Delivery Window</h3>
                    <p class="cp-section-hint">Only additional-service products attach a delivery window to a shipment. Fill in a label to activate it.</p>
                    <div class="cp-grid">
                        <div class="cp-field">
                            <label>Delivery Window Start</label>
                            <input type="time" v-model="productInput.deliveryWindowStart">
                        </div>
                        <div class="cp-field">
                            <label>Delivery Window End</label>
                            <input type="time" v-model="productInput.deliveryWindowEnd">
                        </div>
                        <div class="cp-field">
                            <label>Delivery Window Label</label>
                            <input type="text" v-model="productInput.deliveryWindowLabel" placeholder="Label">
                        </div>
                    </div>
                </div>

                <div class="cp-section">
                    <h3 class="cp-section-title">Pricing & Details</h3>
                    <div class="cp-grid">
                        <div v-if="!useTierPricing" class="cp-field">
                            <label>Price</label>
                            <input type="number" v-model="productInput.price" placeholder="0.00">
                        </div>
                        <div class="cp-field">
                            <label>Fuel Surcharge Code</label>
                            <input type="text" list="createFuelSurchargeList" v-model="productInput.fuelSurchargeCode" @input="capitalizeField('fuelSurchargeCode')" placeholder="Fuel surcharge code">
                            <datalist id="createFuelSurchargeList">
                                <option v-for="(code, index) in distinctFuelSurchargeCodes" :key="index" :value="code" />
                            </datalist>
                        </div>
                    </div>

                    <div class="cp-checks">
                        <label><input type="checkbox" v-model="productInput.additionalService"> Additional Service</label>
                        <label><input type="checkbox" v-model="productInput.defaultProduct"> Default Product</label>
                        <label><input type="checkbox" v-model="useTierPricing" @change="onToggleTierPricing"> Use Tier Pricing</label>
                    </div>

                    <div class="cp-grid cp-grid-2">
                        <div class="cp-field">
                            <label>Description</label>
                            <textarea rows="3" v-model="productInput.description" placeholder="Internal description"></textarea>
                        </div>
                        <div class="cp-field">
                            <label>Description (UI)</label>
                            <textarea rows="3" v-model="productInput.descriptionUI" placeholder="Customer-facing description"></textarea>
                        </div>
                    </div>
                </div>

                <div v-if="useTierPricing" class="cp-section">
                    <h3 class="cp-section-title">Tier Pricing</h3>
                    <p class="cp-section-hint">With tier pricing on, the base price is set to 0 and pricing is driven by the tiers below.</p>
                    <table class="cp-tier-table">
                        <thead>
                            <tr>
                                <th>Start</th><th>End</th><th>Price</th><th>Slab</th><th>Allowed</th><th>Remove</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(tier, tierIdx) in productInput.tierPrice" :key="tierIdx">
                                <td><input type="number" class="cp-tier-input" v-model.number="tier.start"></td>
                                <td><input type="number" class="cp-tier-input" v-model.number="tier.end"></td>
                                <td><input type="number" step="any" class="cp-tier-input" v-model.number="tier.price"></td>
                                <td class="cp-tier-center"><input type="checkbox" v-model="tier.slab"></td>
                                <td class="cp-tier-center"><input type="checkbox" v-model="tier.allowed"></td>
                                <td class="cp-tier-center"><button class="cp-tier-remove" @click="removeProductTier(tierIdx)">×</button></td>
                            </tr>
                            <tr v-if="!productInput.tierPrice || productInput.tierPrice.length === 0">
                                <td colspan="6" class="cp-tier-empty">No tier prices added</td>
                            </tr>
                        </tbody>
                    </table>
                    <button class="cp-tier-add" @click="addProductTier()">+ Add Tier</button>
                    <p v-if="tierPriceWarning" class="cp-tier-error">{{ tierPriceWarning }}</p>
                </div>

                <div class="cp-section">
                    <h3 class="cp-section-title">Included Products</h3>
                    <p class="cp-section-hint">Products automatically included when this product is selected (e.g. a plan that bundles other services).</p>
                    <table class="cp-tier-table">
                        <thead>
                            <tr>
                                <th>Product Code</th><th>Service Code</th><th>Remove</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(included, incIdx) in productInput.includedProducts" :key="incIdx">
                                <td>
                                    <input type="text" list="createProductCodeList" class="cp-tier-input cp-included-input" v-model="included.productCode">
                                </td>
                                <td>
                                    <input type="text" list="createServiceCodeList" class="cp-tier-input cp-included-input" v-model="included.serviceCode">
                                </td>
                                <td class="cp-tier-center"><button class="cp-tier-remove" @click="removeIncludedProduct(incIdx)">×</button></td>
                            </tr>
                            <tr v-if="!productInput.includedProducts || productInput.includedProducts.length === 0">
                                <td colspan="3" class="cp-tier-empty">No included products</td>
                            </tr>
                        </tbody>
                    </table>
                    <button class="cp-tier-add" @click="addIncludedProduct()">+ Add Included Product</button>
                </div>

                <div class="cp-actions">
                    <button class="cp-create-button" @click="createProductItem()">Create Product</button>
                </div>
            </div>
        </div>
        <div v-if="(AdminUser || InternalAdmin)" class="track-shipment update-user pt-wrapper">
            <product-table :key="refreshKey" :jwtToken="jwtToken" :userData="userData" @copyProduct="copyProductToCreate($event)"/>
        </div>
    </div>
</div>
<AlertUser v-if="toggleAlertBox" @closeAlertBox="closeAlertBox($event)" :message="alertMessage"/>
</template>

<script>
import {Auth, API} from 'aws-amplify'
import SignedInUserInfo from '../UserDashboard/SignedInUserInfo.vue'
import AlertUser from '../../../components/Popups/AlertUser.vue';
import ProductTable from '../ProductDashboard/ProductTable.vue'

export default {
    data(){
        return{
            user: {},
            useTierPricing: false,
            productSequence: null,
            serviceGroupSequence: null,
            existingProducts: [],
            serviceNetworkData: {},
            productCodeData: {},
            serviceCodeData: {},
            vendorData: {},
            productFamilyData: {},
            productSequenceData: {},
            serviceGroupData: {},
            serviceGroupSequenceData: {},
            jwtToken: null,
            loadingUser: false,
            userData: {},
            validateCustomAddress: false,
            userCustomAddress: "",
            toggleAlertBox: false,
            AdminUser: false,
            InternalAdmin: false,
            CreateNewAccount: false,
            Customer: false,
            CustomerAdmin: false,
            ManagePrices: false,
            EditorGroup: false,
            productInput: {
                productCode: "",
                serviceCode: "",
                description: "",
                additionalService: false,
                serviceNetwork: "",
                vendor: "",
                price: 0,
                productName: "",
                descriptionUI: "",
                productFamily: null,
                productSequence: null,
                serviceGroup: null,
                serviceGroupSequence: null,
                deliveryWindowEnd: null,
                deliveryWindowStart: null,
                deliveryWindowLabel: null,
                pickupWindowEnd: null,
                pickupWindowStart: null,
                pickupWindowLabel: null,
                fuelSurchargeCode: null,
                defaultProduct: false,
                useTaxCode: false,
                getCost: false,
                discontinued: false,
                tierPrice: [],
                includedProducts: []
            },
            refreshKey: 0
        }
    },
    components:{
        AlertUser,
        ProductTable,
        SignedInUserInfo
    },
    mounted(){
        Auth.currentAuthenticatedUser().then(user => {
            this.user = user;
            this.jwtToken = user.signInUserSession.accessToken.jwtToken;
            this.GetUserGraphQL();
            this.listServiceNetworks();
            this.listProductCode();
            this.listServiceCode();
            this.listVendor();
            this.listServiceGroups();
            this.listProductFamilies();
            this.listProductData();

            if(this.user.signInUserSession.idToken.payload['cognito:groups']){
                this.user.signInUserSession.idToken.payload['cognito:groups'].forEach((group)=>{
                    if(group == 'Admin'){
                      this.AdminUser = true;
                    }

                    if(group == 'InternalAdmin'){
                        this.InternalAdmin = true;
                    }

                    if(group == 'Customer'){
                        this.Customer = true;
                    }

                    if(group == 'CustomerAdmin'){
                        this.CustomerAdmin = true;
                    }

                    if(group == 'ManagePrices'){
                        this.ManagePrices = true;
                    }

                    if(group == 'EditorGroup'){
                        this.EditorGroup = true;
                    }
                })
                if (!this.AdminUser && !this.InternalAdmin) {
                    alert("Access denied to product dashboard")
                    // Redirect to the shipment page
                    this.$router.push('/ShipmentDashboard');
                }
            }
        }).catch(error => {
          if(error){
            this.$router.push('Login');
            Auth.signOut({global: false})
          }
        });
    },
    computed:{
        // Live inline warning for invalid tier ranges (overlap / start > end).
        // Only shown when tier pricing is on and there is at least one tier.
        tierPriceWarning(){
            if(!this.useTierPricing) return null;
            const tiers = this.productInput.tierPrice || [];
            if(tiers.length === 0) return null;
            return this.validateTierPrices(tiers);
        },
        // Prefer values from the dedicated reference tables when they return something;
        // otherwise fall back to distinct values found in the Product table.
        distinctProductCodes(){
            return this.optionsFor(this.productCodeData.items, 'productCode', 'productCode');
        },
        distinctServiceCodes(){
            return this.optionsFor(this.serviceCodeData.items, 'serviceCode', 'serviceCode');
        },
        distinctServiceNetworks(){
            return this.optionsFor(this.serviceNetworkData.items, 'serviceNetwork', 'serviceNetwork');
        },
        distinctVendors(){
            return this.optionsFor(this.vendorData.items, 'vendor', 'vendor');
        },
        distinctProductFamilies(){
            return this.optionsFor(this.productFamilyData.items, 'productFamily', 'productFamily');
        },
        distinctServiceGroups(){
            return this.optionsFor(this.serviceGroupData.items, 'serviceGroup', 'serviceGroup');
        },
        // No dedicated reference table for these, so always use the Product table.
        distinctProductNames(){ return this.distinctValues('productName'); },
        distinctFuelSurchargeCodes(){ return this.distinctValues('fuelSurchargeCode'); }
    },
    watch:{
        'productInput.additionalService'(){
            if(this.productInput.additionalService == true){
                this.productInput.productFamily = null
                this.productInput.productSequence = null
            }

            if(this.productInput.additionalService == false){
                this.productInput.productSequence = this.productSequence
                this.productInput.serviceGroup = null
                this.productInput.deliveryWindowEnd = null
                this.productInput.deliveryWindowStart = null
                this.productInput.deliveryWindowLabel = null
            }
            // Re-evaluate the service group sequence after service group may have changed.
            this.applyServiceGroupSequence()
        },
        'productInput.serviceGroup'(){
            this.productInput.deliveryWindowEnd = null
            this.productInput.deliveryWindowStart = null
            this.productInput.deliveryWindowLabel = null
            // A service group sequence is only needed when a service group is set.
            this.applyServiceGroupSequence()
        }
    },
    methods:{
        distinctValues(field){
            const set = new Set();
            (this.existingProducts || []).forEach(p => {
                if(p && p[field]) set.add(p[field]);
            });
            return Array.from(set).sort();
        },
        sanitizeLettersUpper(field){
            // Keep only letters (A-Z) and force uppercase.
            const value = this.productInput[field] || "";
            this.productInput[field] = value.replace(/[^a-zA-Z]/g, "").toUpperCase();
        },
        capitalizeField(field){
            // Force uppercase (allows letters and numbers).
            const value = this.productInput[field] || "";
            this.productInput[field] = value.toUpperCase();
        },
        applyServiceGroupSequence(){
            // Only assign a service group sequence when a service group is actually set.
            if(this.productInput.serviceGroup){
                this.productInput.serviceGroupSequence = this.serviceGroupSequence;
            }else{
                this.productInput.serviceGroupSequence = null;
            }
        },
        copyProductToCreate(product){
            if(!product) return;
            const tierPrice = Array.isArray(product.tierPrice)
                ? product.tierPrice.map(t => ({ ...t }))
                : [];
            const includedProducts = Array.isArray(product.includedProducts)
                ? product.includedProducts.map(p => ({ productCode: p.productCode, serviceCode: p.serviceCode }))
                : [];

            // Populate the create form from the copied product.
            this.productInput = {
                productCode: product.productCode || "",
                serviceCode: product.serviceCode || "",
                description: product.description || "",
                additionalService: !!product.additionalService,
                serviceNetwork: product.serviceNetwork || "",
                vendor: product.vendor || "",
                price: product.price != null ? product.price : 0,
                productName: product.productName || "",
                descriptionUI: product.descriptionUI || "",
                productFamily: product.productFamily || null,
                productSequence: product.productSequence || null,
                serviceGroup: product.serviceGroup || null,
                serviceGroupSequence: product.serviceGroupSequence || null,
                deliveryWindowEnd: product.deliveryWindowEnd || null,
                deliveryWindowStart: product.deliveryWindowStart || null,
                deliveryWindowLabel: product.deliveryWindowLabel || null,
                pickupWindowEnd: null,
                pickupWindowStart: null,
                pickupWindowLabel: null,
                fuelSurchargeCode: product.fuelSurchargeCode || null,
                defaultProduct: !!product.defaultProduct,
                useTaxCode: !!product.useTaxCode,
                getCost: !!product.getCost,
                discontinued: false,
                tierPrice,
                includedProducts
            };

            // Sync the tier pricing toggle to whatever the copied product had.
            this.useTierPricing = tierPrice.length > 0;

            this.toggleAlertBox = true;
            this.alertMessage = "Copied product into the Create Product form. Update the details and create.";

            // The serviceGroup/additionalService watchers can clear delivery windows,
            // so re-apply them after those have run.
            this.$nextTick(() => {
                this.productInput.deliveryWindowStart = product.deliveryWindowStart || null;
                this.productInput.deliveryWindowEnd = product.deliveryWindowEnd || null;
                this.productInput.deliveryWindowLabel = product.deliveryWindowLabel || null;
                if(this.$refs.createCard){
                    this.$refs.createCard.scrollIntoView({ behavior: "smooth", block: "start" });
                }
            });
        },
        onToggleTierPricing(){
            if(this.useTierPricing){
                // Tier pricing drives the price, so force base price to 0.
                this.productInput.price = 0;
                if(!Array.isArray(this.productInput.tierPrice) || this.productInput.tierPrice.length === 0){
                    this.productInput.tierPrice = [];
                    this.addProductTier();
                }
            }else{
                // Turned off: clear tiers so they aren't sent.
                this.productInput.tierPrice = [];
            }
        },
        addProductTier(){
            if(!Array.isArray(this.productInput.tierPrice)) this.productInput.tierPrice = [];
            this.productInput.tierPrice.push({ start: 0, end: 0, price: 0, slab: false, allowed: true });
        },
        removeProductTier(index){
            this.productInput.tierPrice.splice(index, 1);
        },
        validateTierPrices(tiers){
            // Returns an error message string if invalid, otherwise null.
            // Prices may be negative, so only start/end ranges are validated.
            const list = Array.isArray(tiers) ? tiers : [];
            if(list.length === 0){
                return "Add at least one tier price or turn off tier pricing.";
            }

            // Each tier must have numeric start/end with start <= end.
            for(let i = 0; i < list.length; i++){
                const start = Number(list[i].start);
                const end = Number(list[i].end);
                if(Number.isNaN(start) || Number.isNaN(end)){
                    return `Tier ${i + 1} must have a numeric start and end.`;
                }
                if(start > end){
                    return `Tier ${i + 1} start (${start}) cannot be greater than its end (${end}).`;
                }
            }

            // No two tiers may overlap. Ranges are inclusive on both ends,
            // so two tiers overlap when one's start is <= the other's end and vice versa.
            const sorted = [...list].sort((a, b) => Number(a.start) - Number(b.start));
            for(let i = 1; i < sorted.length; i++){
                const prevEnd = Number(sorted[i - 1].end);
                const currStart = Number(sorted[i].start);
                if(currStart <= prevEnd){
                    return `Tier ranges overlap: ${sorted[i - 1].start}-${sorted[i - 1].end} and ${sorted[i].start}-${sorted[i].end}. Adjust the start/end so tiers do not overlap.`;
                }
            }

            return null;
        },
        addIncludedProduct(){
            if(!Array.isArray(this.productInput.includedProducts)) this.productInput.includedProducts = [];
            this.productInput.includedProducts.push({ productCode: "", serviceCode: "" });
        },
        removeIncludedProduct(index){
            this.productInput.includedProducts.splice(index, 1);
        },
        // Use the reference-table items if present; otherwise fall back to Product-table values.
        optionsFor(referenceItems, referenceField, productField){
            if(Array.isArray(referenceItems) && referenceItems.length > 0){
                const set = new Set();
                referenceItems.forEach(item => {
                    if(item && item[referenceField]) set.add(item[referenceField]);
                });
                if(set.size > 0) return Array.from(set).sort();
            }
            return this.distinctValues(productField);
        },
        async listProductData(){
            let graphqlQuery = `query listProducts{
                listProducts{
                    items{
                        productCode
                        serviceCode
                        serviceNetwork
                        vendor
                        productFamily
                        serviceGroup
                        productName
                        productSequence
                        serviceGroupSequence
                    }
                }
            }`
            
            try{
                let listProductDataReturn = await API.graphql({
                query:  graphqlQuery,
                authMode: "AMAZON_COGNITO_USER_POOLS",
                })

                // Keep the full product list so the Create form can suggest existing values.
                this.existingProducts = listProductDataReturn.data.listProducts.items || [];
                
                let highestProductSequence = null;
                let highestServiceGroupSequence = null;

                for (const item of listProductDataReturn.data.listProducts.items) {
                    const productSequence = item.productSequence !== null ? item.productSequence : null;
                    const serviceGroupSequence = item.serviceGroupSequence !== null ? item.serviceGroupSequence : null;

                    if (highestProductSequence === null || highestProductSequence < productSequence) {
                        highestProductSequence = productSequence;
                    }

                    if (highestServiceGroupSequence === null || highestServiceGroupSequence < serviceGroupSequence) {
                        highestServiceGroupSequence = serviceGroupSequence;
                    }
                }
                this.productSequence = this.incrementWithLeadingZeros(highestProductSequence)
                this.serviceGroupSequence = this.incrementWithLeadingZeros(highestServiceGroupSequence)
                this.productInput.productSequence = this.productSequence
            }catch(e){
                console.log(e)
                if(e){
                    this.toggleAlertBox = true;
                    this.alertMessage = "Error getting product sequence codes"
                }
            }
    },
    incrementWithLeadingZeros(str) {
        const number = parseInt(str, 10);
        const incrementedNumber = number + 1;
        const formattedResult = String(incrementedNumber).padStart(str.length, '0');
        return formattedResult;
    },
        async listServiceGroups(){
            this.loadingUser = true;
            try {
                let listServiceGroupsData = await API.graphql({
                    query: `query listServiceGroups{ listServiceGroups(limit: 1000){ items{ serviceGroup } } }`,
                    authMode: "AMAZON_COGNITO_USER_POOLS",
                })
                this.serviceGroupData = listServiceGroupsData.data.listServiceGroups;
            } catch (error) {
                this.toggleAlertBox = true;
                this.alertMessage = "Unable to get service group codes"
            }finally{
                this.loadingUser = false;
            }
        },
        async listProductFamilies(){
            this.loadingUser = true;
            try {
                let listProductFamiliesData = await API.graphql({
                    query: `query listProductFamilies{ listProductFamilies(limit: 1000){ items{ productFamily } } }`,
                    authMode: "AMAZON_COGNITO_USER_POOLS",
                })
                this.productFamilyData = listProductFamiliesData.data.listProductFamilies;
            } catch (error) {
                this.toggleAlertBox = true;
                this.alertMessage = "Unable to get product family codes"
            }finally{
                this.loadingUser = false;
            }
        },
        async listVendor(){
            this.loadingUser = true;
            try {
                let listVendorData = await API.graphql({
                    query: `query listVendors{ listVendors(limit: 1000){ items{ vendor } } }`,
                    authMode: "AMAZON_COGNITO_USER_POOLS",
                })
                this.vendorData = listVendorData.data.listVendors;
            } catch (error) {
                this.toggleAlertBox = true;
                this.alertMessage = "Unable to get vendor codes"
            }finally{
                this.loadingUser = false;
            }
        },
        async listServiceCode(){
            this.loadingUser = true;
            try {
                let listServiceCodeData = await API.graphql({
                    query: `query listServiceCodes{ listServiceCodes(limit: 1000){ items{ serviceCode } } }`,
                    authMode: "AMAZON_COGNITO_USER_POOLS",
                })
                this.serviceCodeData = listServiceCodeData.data.listServiceCodes;
            } catch (error) {
                this.toggleAlertBox = true;
                this.alertMessage = "Unable to get service codes"
            }finally{
                this.loadingUser = false;
            }
        },
        async listProductCode(){
            this.loadingUser = true;
            try {
                let listProductCodeData = await API.graphql({
                    query: `query listProductCodes{ listProductCodes(limit: 1000){ items{ productCode } } }`,
                    authMode: "AMAZON_COGNITO_USER_POOLS",
                })
                this.productCodeData = listProductCodeData.data.listProductCodes;
            } catch (error) {
                this.toggleAlertBox = true;
                this.alertMessage = "Unable to get product codes"
            }finally{
                this.loadingUser = false;
            }
        },
        async listServiceNetworks(){
            this.loadingUser = true;
            try {
                let listServiceNetworkData = await API.graphql({
                    query: `query listServiceNetworks{ listServiceNetworks(limit: 1000){ items{ serviceNetwork } } }`,
                    authMode: "AMAZON_COGNITO_USER_POOLS",
                })
                this.serviceNetworkData = listServiceNetworkData.data.listServiceNetworks;
            } catch (error) {
                this.toggleAlertBox = true;
                this.alertMessage = "Unable to get service network information"
            }finally{
                this.loadingUser = false;
            }
        },
        async createProductItem(){
            this.loadingUser = true;
            await this.createProduct();
            this.loadingUser = false;
        },
        async GetUserGraphQL(){
            this.loadingUser = true;
            try {
                let userReturnData = await API.graphql({
                    query: `query GetUser($userName: String!){
                        getUser(userName: $userName){
                        userName
                        accountNo
                        fullname
                        locationID
                        userEmail
                        locationAddress{
                        labelImage
                        serviceAddress{
                            address{
                                companyName
                                attention
                                address1
                                address2
                                city
                                state
                                zipCode
                                phone
                                phoneExt
                                relevance
                                latitude
                                longitude
                                }
                        }
                        }
                        labelImage
                    }
                    }`,
                    variables: {
                            userName: this.user.username
                    },
                    authMode: "AMAZON_COGNITO_USER_POOLS",
                })
                this.userData = userReturnData.data.getUser;
            } catch (error) {
                this.toggleAlertBox = true;
                this.alertMessage = "Unable to get user information"
            }finally{
                this.loadingUser = false;
            }
        },
        async createProduct(){
            this.loadingUser = true;

            if(this.productInput.serviceGroup == 'Delivery Window'){
                if(this.productInput.deliveryWindowLabel == null){
                    this.toggleAlertBox = true;
                    this.alertMessage = "You must add a delivery label"
                    return
                }
                if(this.productInput.deliveryWindowEnd == null){
                    this.toggleAlertBox = true;
                    this.alertMessage = "You must add a delivery window end"
                    return
                }
                if(this.productInput.deliveryWindowStart == null){
                    this.toggleAlertBox = true;
                    this.alertMessage = "You must add a delivery window start"
                    return
                }
            }

            if(this.productInput.productCode == ""){
                this.toggleAlertBox = true;
                this.alertMessage = "You must add a product code"
                return
            }

            if(this.productInput.serviceCode == ""){
                this.toggleAlertBox = true;
                this.alertMessage = "You must add a service code"
                return
            }

            if(this.productInput.serviceNetwork == ""){
                this.toggleAlertBox = true;
                this.alertMessage = "You must add a service network"
                return
            }

            if(this.productInput.productName == ""){
                this.toggleAlertBox = true;
                this.alertMessage = "You must add a product name"
                return
            }

            if(this.productInput.description == ""){
                this.toggleAlertBox = true;
                this.alertMessage = "You must add a description"
                return
            }

            if(this.productInput.descriptionUI == ""){
                this.toggleAlertBox = true;
                this.alertMessage = "You must add a UI description"
                return
            }

            // Drop any blank included-product rows before sending.
            this.productInput.includedProducts = (this.productInput.includedProducts || [])
                .filter(item => item.productCode && item.serviceCode);

            // When tier pricing is used, the base price stays 0 and tiers drive pricing.
            if(this.useTierPricing){
                this.productInput.price = 0;

                // Validate the tier ranges (no overlap, start <= end).
                const tierError = this.validateTierPrices(this.productInput.tierPrice);
                if(tierError){
                    this.toggleAlertBox = true;
                    this.alertMessage = tierError;
                    this.loadingUser = false;
                    return;
                }
            }else{
                this.productInput.tierPrice = [];
            }

            // Inline mutation that avoids selecting non-nullable createdAt/updatedAt,
            // which older Product records don't have.
            const createProductMutation = `mutation CreateProduct($input: CreateProductInput!){
                createProduct(input: $input){
                    productCode
                    serviceCode
                }
            }`;

            try {
                await API.graphql({
                    query: createProductMutation,
                    variables: {
                        input: this.productInput
                    },
                    authMode: "AMAZON_COGNITO_USER_POOLS",
                })
                this.refreshKey++
                this.toggleAlertBox = true;
                this.alertMessage = "Created Product"
            } catch (error) {
                console.log(error)
                if(error.errors[0].message){
                    this.toggleAlertBox = true;
                    this.alertMessage = error.errors[0].message
                    this.loadingUser = false;
                }else{
                    this.toggleAlertBox = true;
                    this.alertMessage = "Unable to create product."
                    this.loadingUser = false;
                }
                
            }
        },
        closeAlertBox(toggleAlertBox){
            this.toggleAlertBox = toggleAlertBox;
        }
    }
}
</script>

<style scoped>
.st0{fill:none;stroke:#000000;stroke-width:10;stroke-linecap:round;stroke-linejoin:round;}

.cls-1 {
    fill: none;
    stroke: #3f3f3f;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 10px;
}

.cls-2 {
    fill: none;
    stroke: #000;
    stroke-miterlimit: 10;
    stroke-width: 8px;
}

.cls-2-alt{
    stroke: #000;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 11px;
}

.cp-wrapper{
    flex-basis: 92%;
    width: auto;
    align-items: stretch;
}

/* Manage Products table wrapper: full width, stretch, and constrain so the
   inner table scrolls horizontally instead of overflowing the page. */
.pt-wrapper{
    flex-basis: 92% !important;
    width: auto;
    max-width: 100%;
    align-items: stretch;
    box-sizing: border-box;
    overflow: hidden;
}

.cp-card{
    width: 100%;
    text-align: left;
}

.cp-header{
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 14px;
    padding-bottom: 12px;
    border-bottom: 1px solid #eee;
    margin-bottom: 16px;
}

.cp-header-icon{
    background-color: #e4e4e4;
    border-radius: 14px;
    padding: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.cp-header-icon svg{
    width: 34px;
    height: 34px;
}

.cp-title{
    margin: 0;
    font-size: 20px;
}

.cp-subtitle{
    margin: 2px 0 0 0;
    font-size: 13px;
    color: #6a6a6a;
}

.cp-section{
    margin-bottom: 18px;
}

.cp-section-title{
    margin: 0 0 10px 0;
    font-size: 14px;
    color: #3f3f3f;
    padding-left: 10px;
    border-left: 3px solid #32ccfe;
}

.cp-section-hint{
    font-size: 12px;
    color: #6a6a6a;
    margin: 0 0 10px 0;
}

.cp-grid{
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 12px;
}

.cp-grid-2{
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    margin-top: 12px;
}

.cp-field{
    display: flex;
    flex-direction: column;
}

.cp-field label{
    font-size: 12px;
    font-weight: bold;
    color: #3f3f3f;
    margin-bottom: 4px;
}

.cp-field input,
.cp-field textarea{
    padding: 10px;
    font-size: 14px;
    border: 1px solid rgba(0,0,0,.2);
    border-radius: 12px;
    outline: none;
    background-color: #fff;
    margin: 0;
    box-sizing: border-box;
    width: 100%;
}

.cp-field input:focus,
.cp-field textarea:focus{
    border-color: #32ccfe;
}

.cp-field input:disabled{
    background-color: #f3f3f3;
    color: #888;
}

.cp-toggle-field{
    flex-direction: row;
    align-items: center;
    gap: 10px;
    align-self: flex-end;
    padding: 10px 0;
}

.cp-toggle-field label{
    margin-bottom: 0;
}

.cp-checks{
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 16px;
    margin-top: 14px;
    font-size: 13px;
}

.cp-checks label{
    display: flex;
    align-items: center;
    gap: 6px;
}

.cp-tier-table{
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    border: 1px solid #eee;
    border-radius: 10px;
    overflow: hidden;
}

.cp-tier-table th,
.cp-tier-table td{
    padding: 6px 10px;
    border-bottom: 1px solid #f0f0f0;
    text-align: left;
}

.cp-tier-table thead th{
    background-color: #f3f3f3;
    font-size: 12px;
}

.cp-tier-center{
    text-align: center;
}

.cp-tier-input{
    width: 90px;
    padding: 6px;
    border: 1px solid rgba(0,0,0,.2);
    border-radius: 8px;
    outline: none;
    margin: 0;
}

.cp-included-input{
    width: 140px;
}

.cp-tier-empty{
    text-align: center;
    color: #6a6a6a;
}

.cp-tier-remove{
    background-color: #fe804d;
    color: #fff;
    border: none;
    border-radius: 999px;
    width: 22px;
    height: 22px;
    cursor: pointer;
    line-height: 1;
}

.cp-tier-add{
    background-color: #32ccfe;
    color: #fff;
    border: none;
    border-radius: 999px;
    padding: 8px 18px;
    font-size: 13px;
    cursor: pointer;
    margin-top: 10px;
}

.cp-tier-add:hover{
    filter: brightness(0.95);
}

.cp-tier-error{
    margin: 10px 0 0 0;
    font-size: 12px;
    color: #c0392b;
    font-weight: bold;
}

.cp-actions{
    display: flex;
    justify-content: flex-end;
    border-top: 1px solid #eee;
    padding-top: 16px;
}

.cp-create-button{
    background-color: #33f18a;
    color: #fff;
    border: none;
    border-radius: 999px;
    padding: 12px 30px;
    font-size: 15px;
    cursor: pointer;
    transition-duration: .25s;
}

.cp-create-button:hover{
    filter: brightness(0.95);
}

.check-box-container{
    margin-top: 5px;
    display: flex;
    flex-direction: row;
    align-items: center;
}

.check-box-container input{
    margin-left: 10px !important;
}

input[type="checkbox"] {
    cursor: pointer;
    -webkit-appearance: none;
    appearance: none;
    /* For iOS < 15 */
    background-color: var(--form-background);
    margin: 0;

    font: inherit;
    color: #33f18a;
    width: 1.05em;
    height: 1.05em;
    border: 0.15em solid #33f18a;
    border-radius: 0.15em;
    padding: 0;
    transform: translateY(-0.075em);

    display: grid;
    place-content: center;
}

input[type="checkbox"]::before {
    content: "";
    width: 0.65em;
    height: 0.65em;
    clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
    transform: scale(0);
    transform-origin: bottom left;
    transition: 120ms transform ease-in-out;
    box-shadow: inset 1em 1em var(--form-control-color);
    /* Windows High Contrast Mode */
    background-color: CanvasText;
}

input[type="checkbox"]:checked::before {
    transform: scale(1);
}

input, select, textarea{
    padding: 10px;
    font-size: 15px;
    border: 1px solid rgba(0,0,0,.336);
    border-radius: 15px;
    outline: none;
    margin-top: 5px;
}


input[type=file]::file-selector-button {
    border: none;
    padding: 5px;
    border-radius: 15px;
    background-color: #32ccfe;
    transition: 1s;
    color: #fff;
    cursor: pointer;
}

input[type=file]::file-selector-button:hover {
    background-color: #2dbbeb;
    transition: 1s;
}

.confirmLabelContainer img{
    width: 50%;
    max-width: 150px;
    margin-top: 5px;
    border-radius: 5px;
}

.file-input{
    width: 80%;
}

html{
    background-color: #f3f3f3;
}

.main-container{
    margin-top: 20px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
}

.inner-container{
    width: 70%;
    background-color: #fff;
    box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
    border-radius: 25px;
    display: flex;
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
}

.service-input-container{
    background-color: #fff;
    box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
    border-radius: 25px;
    padding: 10px;
    margin-top: 5px;
    flex-basis: 30%;
}

.service-levels-inner{
    display: flex;
    flex-direction: column;
    flex-basis: 30%;
}

.confirmContainerInner1{
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    flex-wrap: wrap;
}

.header-track-shipment{
    width: 100%;
    margin-bottom: 5px;
    display: flex;
    flex-direction: row;
    align-items: center;
}

.header-track-inner{
    display: flex;
    flex-direction: row;
    align-items: center;
    width: fit-content;
    background-color: #e2e2e2;
    padding: 5px 15px;
    border-radius: 999px;
}

.header-track-inner svg{
    height: 22px;
    margin: 0;
}

.header-track-shipment p{
    font-size: 18px;
    margin: 0;
    margin-left: 10px;
}

.header-track-shipment{
    width: 100%;
    margin-bottom: 5px;
}

.track-shipment{
    background-color: #fff;
    padding: 15px;
    border-radius: 25px;
    flex-basis: 25%;
    width: fit-content;
    height: fit-content;
    margin: 10px;
    box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
}

.update-user{
    flex-basis: 60%;
}

.confirm-svg{
    display: flex;
    flex-direction: row;
    align-items: center;
    background-color: #e4e4e4;
    border-radius: 999px;
    padding: 5px;
    width: fit-content;
}

.confirm-svg svg{
    width: 35px;
    height: 35px;
    padding-right: 5px;
}

.confirm-svg h2{
    margin: 0;
}

.user-info{
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    text-align: left;
}

.user-info p{
    margin: 0;
}

.confirmContainerInner{
    /* display: flex; */
    flex-direction: row;
    justify-content: space-between;
    text-align: left;
    padding: 10px;
}

.service-levels{
    display: flex;
    flex-direction: column;
    flex-basis: 30%;
}

.auto-complete-container{
    margin-top: 5px !important;
    width: 90%;
    margin-left: 10px;
}

.update-user-button{
    background-color: #e4e4e4;
    color: #000;
    border: none;
    border-radius: 999px;
    padding: 5px 15px;
    width: fit-content;
    margin-top: 5px;
    margin-left: 10px;
    margin-bottom: 5px;
    transition-duration: .5s;
    cursor: pointer;
}

.update-user-button:hover{
    transition-duration: .5s;
    background-color: #d5d5d5;
}

.add-service-button{
    margin: 0;
    margin-top: 5px;
}

@media only screen and (max-width: 1300px) {
    .inner-container{
        width: 85%;
    }
}

@media only screen and (max-width: 1000px) {
    .data-container-main, .inner-track-shipment-container{
        flex-wrap: wrap;
    }

    .track-shipment{
        flex-basis: 70%;
    }

    .data-container{
        flex-basis: 40%;
    }
}

@media only screen and (max-width: 600px) {
    .inner-container{
        width: 95%;
    }

    .data-container{
        flex-basis: 80%;
    }

    .track-shipment{
        flex-basis: 80%;
    }

    /* Give the create form and manage-products table the full width and less
       padding on small screens so their content fits. */
    .cp-wrapper,
    .pt-wrapper{
        flex-basis: 100%;
        width: 100%;
        padding: 12px;
        margin: 10px 0;
    }

    .confirmContainerInner{
        flex-wrap: wrap;
    }

    .notifications{
        margin-top: 5px;
    }
}
</style>